import {
  Color,
  type Data3DTexture,
  Mesh,
  OrthographicCamera,
  PlaneGeometry,
  type RawShaderMaterial,
  Scene,
  Vector2,
  type WebGLRenderer,
} from 'three';
import type { StageCamera } from '../camera/StageCamera';
import type { RenderSize } from '../size';
import { sharedUniforms } from '../materials/toon';
import type { Grade } from '../palette';
import { bindTargets, createPostMaterial, createUpscaleMaterial } from './passes';
import { createTargets, disposeTargets, type FrameTargets } from './targets';

export interface PipelineSettings {
  paletteLock: number;
  ditherAmp: number;
  outlines: boolean;
  vignette: number;
  /** Scanlines locked to the integer scale, and a little more vignette. */
  crt: boolean;
  /** Tiles behind the playfield where the fog starts, and how far until it is full. */
  fogStart: number;
  fogSpan: number;
}

/** The transition's colours (sRGB hex, palette colours): a bubble's body, its rim, the walls between, its shine. */
export interface TransitionColours {
  fill: string;
  rim: string;
  wall: string;
  shine: string;
}

/** A transition bubble's cell and its line width, in base (640x360) units. */
const TRANSITION_CELL = 40;
const TRANSITION_LINE = 2;

/** Display rows the gap between two scanlines is darkened to, by integer scale (index = scale, capped). */
const SCANLINE_GAP = [1, 0.8, 0.78, 0.64, 0.6] as const;

/**
 * The frame (05 §8): scene -> [colour + normal/id + depth] at the internal size -> outline, palette
 * lock, fog, grade, the transition -> integer nearest-neighbour upscale, letterboxed.
 */
export class RenderPipeline {
  // A light touch of the palette lock by default: the owner asked for a less pixelated game
  // (2026-09-23); the 1987 look turns it all the way up.
  readonly settings: PipelineSettings = {
    paletteLock: 0.25,
    ditherAmp: 0.03,
    outlines: true,
    vignette: 0.12,
    crt: false,
    fogStart: 1.5,
    fogSpan: 6,
  };

  private targets: FrameTargets;
  private currentSize: RenderSize;
  private readonly postMaterial: RawShaderMaterial;
  private readonly upscaleMaterial: RawShaderMaterial;
  private readonly quadScene = new Scene();
  private readonly quadCamera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly quad: Mesh;
  private readonly clearColor = new Color();
  private readonly bufferSize = new Vector2();
  private scale = 1;
  /** Where the game image sits on the page, CSS pixels: the HUD lines up with it. */
  readonly viewRect = { left: 0, top: 0, width: 0, height: 0 };

  constructor(
    private readonly renderer: WebGLRenderer,
    lut: Data3DTexture,
    ink: Color,
    size: RenderSize,
  ) {
    this.currentSize = size;
    this.targets = createTargets(size);
    this.postMaterial = createPostMaterial(lut, ink);
    this.upscaleMaterial = createUpscaleMaterial();
    bindTargets(this.postMaterial, this.upscaleMaterial, this.targets, size);
    this.fitTransition();
    this.quad = new Mesh(new PlaneGeometry(2, 2), this.postMaterial);
    this.quad.frustumCulled = false;
    this.quadScene.add(this.quad);
  }

  /** The internal resolution (05 §3). */
  get size(): RenderSize {
    return this.currentSize;
  }

  /**
   * A new internal resolution (the pixel size option, 08 §4): new targets at `size`, the old ones freed.
   * The caller makes a camera for it too.
   */
  resize(size: RenderSize): void {
    const old = this.targets;
    this.targets = createTargets(size);
    this.currentSize = size;
    bindTargets(this.postMaterial, this.upscaleMaterial, this.targets, size);
    this.fitTransition();
    disposeTargets(old);
  }

  /** Integer scale currently used for the blow-up. */
  get integerScale(): number {
    return this.scale;
  }

  /** The quantisation LUT; returns the one it replaces (the caller frees it if it made it). */
  setLut(lut: Data3DTexture): Data3DTexture {
    const u = this.uniform(this.postMaterial, 'tLut');
    const old = u.value as Data3DTexture;
    u.value = lut;
    return old;
  }

  /** Outline colour for Bub and Bob; null for the normal ink. */
  setPlayerRim(hex: string | null): void {
    const post = this.postMaterial;
    if (hex === null) {
      this.uniform(post, 'uPlayerRim').value = this.uniform(post, 'uInk').value;
      return;
    }
    const c = new Color(hex).convertLinearToSRGB();
    this.uniform(post, 'uPlayerRim').value = [c.r, c.g, c.b];
  }

  /** Fog colour (also the clear colour) and the post-lock grade. */
  setGrade(grade: Grade, fogHex: string): void {
    const post = this.postMaterial;
    const fog = new Color(fogHex).convertLinearToSRGB();
    this.uniform(post, 'uFog').value = [fog.r, fog.g, fog.b];
    this.uniform(post, 'uFogAmount').value = grade.fogAmount;
    const tint = new Color(grade.tint).convertLinearToSRGB();
    this.uniform(post, 'uTint').value = [tint.r, tint.g, tint.b];
    this.uniform(post, 'uTintAmount').value = grade.tintAmount;
    this.uniform(post, 'uValueScale').value = grade.valueScale;
    this.clearColor.set(fogHex);
  }

  /** The screen transition's phase (render/game/transition.ts): 0 off, 0..1 covering, 1..2 uncovering. */
  setTransition(phase: number): void {
    this.uniform(this.postMaterial, 'uTransPhase').value = phase;
  }

  setTransitionColours(c: TransitionColours): void {
    const srgb = (hex: string): number[] => {
      const s = new Color(hex).convertLinearToSRGB();
      return [s.r, s.g, s.b];
    };
    this.uniform(this.postMaterial, 'uTransFill').value = srgb(c.fill);
    this.uniform(this.postMaterial, 'uTransRim').value = srgb(c.rim);
    this.uniform(this.postMaterial, 'uTransWall').value = srgb(c.wall);
    this.uniform(this.postMaterial, 'uTransShine').value = srgb(c.shine);
  }

  /** The transition's bubbles keep their size on screen whatever the internal resolution. */
  private fitTransition(): void {
    const cell = TRANSITION_CELL * this.currentSize.scale;
    this.uniform(this.postMaterial, 'uTransCell').value = cell;
    this.uniform(this.postMaterial, 'uTransLine').value = Math.max(1, TRANSITION_LINE * this.currentSize.scale) / cell;
  }

  /** Post-pass pixels (RGBA8, bottom row first, border included): for automated look checks. */
  readPixels(): Uint8Array {
    const { targetW, targetH } = this.currentSize;
    const out = new Uint8Array(targetW * targetH * 4);
    this.renderer.readRenderTargetPixels(this.targets.post, 0, 0, targetW, targetH, out);
    return out;
  }

  render(scene: Scene, stage: StageCamera): void {
    const renderer = this.renderer;
    const camera = stage.camera;
    sharedUniforms.uCamTexel.value.copy(stage.camTexel);

    renderer.setRenderTarget(this.targets.scene);
    renderer.setClearColor(this.clearColor, 0);
    renderer.clear(true, true, true);
    renderer.render(scene, camera);

    const post = this.postMaterial;
    const s = this.settings;
    this.uniform(post, 'uNear').value = camera.near;
    this.uniform(post, 'uFar').value = camera.far;
    this.uniform(post, 'uCamZ').value = camera.position.z;
    this.uniform(post, 'uHalfH').value = camera.top;
    this.uniform(post, 'uSinPitch').value = stage.sinPitch;
    this.uniform(post, 'uCosPitch').value = stage.cosPitch;
    this.uniform(post, 'uFogStart').value = s.fogStart;
    this.uniform(post, 'uFogSpan').value = s.fogSpan;
    this.uniform(post, 'uPaletteLock').value = s.paletteLock;
    this.uniform(post, 'uDitherAmp').value = s.ditherAmp;
    this.uniform(post, 'uOutlines').value = s.outlines ? 1 : 0;
    this.quad.material = post;
    renderer.setRenderTarget(this.targets.post);
    renderer.render(this.quadScene, this.quadCamera);

    this.upscale(stage);
  }

  private upscale(stage: StageCamera): void {
    const renderer = this.renderer;
    const up = this.upscaleMaterial;
    (this.uniform(up, 'uSubTexel').value as Vector2).copy(stage.subTexel);
    this.uniform(up, 'uVignette').value = this.settings.vignette;
    this.quad.material = up;
    renderer.setRenderTarget(null);
    renderer.getDrawingBufferSize(this.bufferSize);
    const bw = this.bufferSize.x;
    const bh = this.bufferSize.y;
    const { viewW, viewH } = this.currentSize;
    this.scale = Math.max(1, Math.floor(Math.min(bw / viewW, bh / viewH)));
    const crt = this.settings.crt;
    const gap = SCANLINE_GAP[Math.min(SCANLINE_GAP.length - 1, this.scale)] ?? 1;
    this.uniform(up, 'uScale').value = this.scale;
    this.uniform(up, 'uScanline').value = crt ? gap : 1;
    this.uniform(up, 'uVignetteBoost').value = crt ? 0.14 : 0;
    const w = viewW * this.scale;
    const h = viewH * this.scale;
    const pr = renderer.getPixelRatio();
    renderer.setClearColor(0x120e1c, 1);
    renderer.setViewport(0, 0, bw / pr, bh / pr);
    renderer.clear(true, false, false);
    const left = Math.floor((bw - w) / 2) / pr;
    const bottom = Math.floor((bh - h) / 2) / pr;
    renderer.setViewport(left, bottom, w / pr, h / pr);
    this.viewRect.left = left;
    this.viewRect.top = bh / pr - bottom - h / pr;
    this.viewRect.width = w / pr;
    this.viewRect.height = h / pr;
    renderer.render(this.quadScene, this.quadCamera);
  }

  /** Free GPU resources. */
  dispose(): void {
    disposeTargets(this.targets);
    this.postMaterial.dispose();
    this.upscaleMaterial.dispose();
    this.quad.geometry.dispose();
  }

  private uniform(material: RawShaderMaterial, name: string): { value: unknown } {
    const u = material.uniforms[name];
    if (!u) throw new Error(`missing uniform ${name}`);
    return u;
  }
}
